import { useState, type ReactElement } from "react";

import { EXECUTION_BOOKMAKERS } from "./place-bet-dialog.js";

/**
 * Correcting a bet that is already logged.
 *
 * A bet is struck on a phone at whatever number was on the screen at that
 * second, and what the app records is what it was told at placement. When
 * that turns out to be wrong there was no way to fix it, and three things
 * quietly went wrong: the profit, the closing-line comparison, and which
 * account gets credited with the wear - the number the whole anti-limiting
 * strategy turns on.
 *
 * Only the book and the price. The line, the stake and the result are not
 * corrections, they are different bets or different outcomes, and letting a
 * dialog rewrite them would make the ledger unfalsifiable.
 */
export function AmendBetDialog({
  bet,
  onCancel,
  onAmend,
}: {
  readonly bet: {
    readonly betId: string;
    readonly sport: string;
    readonly description: string;
    readonly bookmaker: string;
    readonly actualPrice: number;
  };
  readonly onCancel: () => void;
  readonly onAmend: (input: {
    sport: string;
    betId: string;
    bookmaker?: string;
    actualPrice?: number;
  }) => Promise<void>;
}): ReactElement {
  const [bookmaker, setBookmaker] = useState(bet.bookmaker);
  const [price, setPrice] = useState(String(bet.actualPrice));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const parsedPrice = Number(price);
  const priceValid = Number.isFinite(parsedPrice) && parsedPrice > 1;
  const changed =
    bookmaker !== bet.bookmaker ||
    (priceValid && parsedPrice !== bet.actualPrice);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onCancel}>
      <section
        className="dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="amend-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">Correct a logged bet</span>
            <h2 id="amend-title">{bet.description}</h2>
          </div>
        </header>
        <div className="dialog-body">
          <label>
            Bookmaker
            <select
              value={bookmaker}
              onChange={(event) => setBookmaker(event.target.value)}
            >
              {EXECUTION_BOOKMAKERS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label>
            Price taken
            <input
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          </label>
          {/* Said out loud, because a settled winner's profit moves with the
              price, and a silent change to a number already banked is the kind
              of thing that gets noticed weeks later in a total that will not
              reconcile. */}
          <label className="value-check">
            <span>
              A settled winner&rsquo;s profit is recalculated from the new
              price. The correction is recorded against the bet.
            </span>
          </label>
          {error === null ? null : (
            <label className="value-check fail">
              <strong>{error}</strong>
            </label>
          )}
        </div>
        <footer>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!changed || !priceValid || saving}
            onClick={() => {
              setSaving(true);
              setError(null);
              void onAmend({
                sport: bet.sport,
                betId: bet.betId,
                ...(bookmaker === bet.bookmaker ? {} : { bookmaker }),
                ...(parsedPrice === bet.actualPrice
                  ? {}
                  : { actualPrice: parsedPrice }),
              })
                .catch((cause: unknown) => {
                  setError(
                    cause instanceof Error
                      ? cause.message
                      : "The correction could not be saved.",
                  );
                })
                .finally(() => {
                  setSaving(false);
                });
            }}
          >
            {saving ? "Saving…" : "Save correction"}
          </button>
        </footer>
      </section>
    </div>
  );
}
